/**
 * Wraps a page that needs an account (DECISIONS.md #87: every online match does). Anonymous
 * visitors go to the login page and come back here afterwards; the first render waits for the
 * session check so a logged-in reload does not bounce through the login page.
 */
import { colors, fonts } from '@steelwake/ui';
import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';

import { useAppStore } from '../store.js';

export function RequireAuth({ children }: { children: ReactNode }) {
  const authStatus = useAppStore((s) => s.authStatus);
  const location = useLocation();
  if (authStatus === 'unknown') {
    return (
      <p
        data-testid="auth-loading"
        role="status"
        style={{ fontFamily: fonts.ui, color: colors.textMuted }}
      >
        Checking your account…
      </p>
    );
  }
  if (authStatus === 'anonymous') {
    const next = encodeURIComponent(`${location.pathname}${location.search}`);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  return <>{children}</>;
}

/** Only same-site paths may be used as a post-login destination. */
export function safeNext(search: string): string | null {
  const next = new URLSearchParams(search).get('next');
  if (!next || !next.startsWith('/') || next.startsWith('//')) return null;
  return next;
}
